import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatNumber } from 'shared/utils';
import { useRevealedCount } from 'shared/hooks/search-results-paging.hooks';
import { createSearchPagingReporter } from 'shared/helpers/src/search-paging-analytics';
import type { SearchType } from 'shared/helpers/src/search-impression-properties';
import searchContextIdManager from 'shared/helpers/src/search-context-id-manager';
import analyticsService from 'src/helpers/analytics.service';
import { useLiveRegion } from 'src/hooks/accessibility/use-live-region';

interface ShowMoreResultsOptions {
  total: number;
  resetKey: unknown;
  searchType: SearchType;
  section: string;
  sectionType: string;
  sectionPosition: number;
}

const reporter = createSearchPagingReporter((event, properties) =>
  analyticsService.track(event, properties),
);

/**
 * The "Show more" behaviour one tier of a search-v2 group shares with the other: how
 * many of `total` rows to render (`useRevealedCount`), the control's label, and a press
 * handler that reports `[Search] Show more results` for the tier (`searchType`), reveals
 * the next batch and announces it to screen readers. Analytics failures are contained
 * so a press always reveals.
 *
 * @param options - `total` rows in the tier, the `resetKey` that changes with the
 *   search, the tier's `searchType` and the section's name/type/position for the event.
 * @returns `visibleCount` rows to render, `remaining` rows hidden, `nextBatch` the next
 *   press reveals, the control's `label`, `onShowMore` and the `liveRegion` to render.
 * @example
 * const { visibleCount, remaining, label, onShowMore, liveRegion } = useShowMoreResults({
 *   total: entries.length,
 *   resetKey,
 *   searchType: SEARCH_TYPE.EXTENDED,
 *   section,
 *   sectionType,
 *   sectionPosition,
 * });
 */
export function useShowMoreResults({
  total,
  resetKey,
  searchType,
  section,
  sectionType,
  sectionPosition,
}: ShowMoreResultsOptions) {
  const lang = useSelector(getLang('SEARCH_SCREEN'));
  const term = useSelector((state: { search: { term: string } }) => state.search.term);
  const searchSessionId = useSelector(
    (state: { search: { sessionId?: string } }) => state.search.sessionId,
  );
  const { visibleCount, remaining, nextBatch, showMore } = useRevealedCount({ total, resetKey });
  const { announce, liveRegion } = useLiveRegion();

  const onShowMore = () => {
    try {
      reporter.showMoreResults({
        term,
        searchSessionId,
        // Read at click time: the manager is a module singleton, not React state.
        contextId: searchContextIdManager.getContextId(),
        searchType,
        section,
        sectionType,
        sectionPosition,
        visibleCount,
        revealedCount: nextBatch,
        remainingCount: remaining - nextBatch,
      });
    } catch (error) {
      console.warn('[search-v2] show more analytics failed', error);
    }
    showMore();
    announce(
      lang.MORE_RESULTS_SHOWN.replace('{number}', formatNumber(nextBatch)).replace(
        '{remaining}',
        formatNumber(remaining - nextBatch),
      ),
    );
  };

  return {
    visibleCount,
    remaining,
    nextBatch,
    label: lang.SHOW_MORE_RESULTS.replace('{number}', formatNumber(nextBatch)),
    onShowMore,
    liveRegion,
  };
}
